if (!customElements.get('nsss-shipping-estimator')) {
  customElements.define('nsss-shipping-estimator', class extends HTMLElement {
    connectedCallback() {
      this.life?.abort();
      this.life = new AbortController();
      const options = { signal: this.life.signal };
      this.country = this.querySelector('[data-country]');
      this.province = this.querySelector('[data-province]');
      this.postal = this.querySelector('[data-postal]');
      this.button = this.querySelector('[data-calculate]');
      this.result = this.querySelector('[data-result]');
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.root = window.Shopify?.routes?.root || '/';
      this.country.addEventListener('change', () => this.updateProvinces(), options);
      this.addEventListener('input', () => this.invalidate(), options);
      this.addEventListener('change', () => this.invalidate(), options);
      this.button.addEventListener('click', () => this.calculate(), options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target === this.postal) {
          event.preventDefault();
          this.calculate();
        }
      }, options);
      for (const type of ['cart:update', 'discount:update', 'quantity-selector:update']) {
        document.addEventListener(type, () => this.invalidate(), options);
      }
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.invalidate();
      }, options);
      window.addEventListener('pageshow', () => this.invalidate(), options);
      this.updateProvinces();
      this.invalidate();
    }
    disconnectedCallback() {
      this.life?.abort();
      this.request?.abort();
    }
    updateProvinces() {
      let provinces = [];
      try { provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
      this.province.replaceChildren(new Option(this.messages.choose_province, ''));
      for (const [value, label] of provinces) this.province.add(new Option(label, value));
      this.province.disabled = !provinces.length;
      this.querySelector('[data-province-wrap]').hidden = !provinces.length;
    }
    invalidate() {
      this.request?.abort();
      this.button.disabled = false;
      this.removeAttribute('aria-busy');
      if (this.hasEstimate) this.show('stale');
    }
    show(key) {
      this.result.textContent = this.messages[key];
    }
    fingerprint(cart) {
      return JSON.stringify([cart.currency, cart.total_price, cart.total_weight,
        cart.items.map(item => [item.key, item.quantity, item.final_line_price])]);
    }
    async calculate() {
      if (this.button.disabled) return;
      this.country.required = true;
      this.province.required = !this.province.disabled;
      const valid = [this.country, this.province, this.postal].every(field => field.disabled || field.reportValidity());
      this.country.required = this.province.required = false;
      if (!valid) return;
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const { signal } = controller;
      const timeout = setTimeout(() => controller.abort(new Error('timeout')), 20000);
      const fetchJSON = async (path, options = {}) => {
        const response = await fetch(this.root + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal });
        if (!response.ok) throw new Error('request');
        return response.json();
      };
      this.button.disabled = true;
      this.hasEstimate = true;
      this.setAttribute('aria-busy', 'true');
      this.show('loading');
      try {
        const before = await fetchJSON('cart.js');
        if (!before.item_count) { this.show('empty'); return; }
        if (!before.requires_shipping) { this.show('no_shipping'); return; }
        const params = new URLSearchParams({
          'shipping_address[country]': this.country.value,
          'shipping_address[zip]': this.postal.value.trim()
        });
        if (!this.province.disabled) params.set('shipping_address[province]', this.province.value);
        const query = params.toString();
        await fetchJSON(`cart/prepare_shipping_rates.json?${query}`, { method: 'POST' });
        let rates;
        for (let attempt = 0; attempt < 12; attempt++) {
          if (signal.aborted) throw signal.reason;
          const response = await fetchJSON(`cart/async_shipping_rates.json?${query}`);
          if (response !== null) { rates = response.shipping_rates; break; }
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        if (signal.aborted) throw signal.reason;
        if (!Array.isArray(rates)) { this.show('timeout'); return; }
        const after = await fetchJSON('cart.js');
        if (signal.aborted) throw signal.reason;
        if (this.fingerprint(before) !== this.fingerprint(after)) { this.show('stale'); return; }
        if (!rates.length) { this.show('no_rates'); return; }
        if (after.currency !== this.dataset.currency && rates.some(rate => !rate.currency)) { this.show('currency_unavailable'); return; }
        const list = document.createElement('ul');
        for (const rate of rates) {
          const amount = Number(rate.price);
          if (!Number.isFinite(amount)) throw new Error('rate');
          const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency: rate.currency || this.dataset.currency, currencyDisplay: 'code'
          }).format(amount);
          const item = document.createElement('li');
          item.textContent = `${rate.presentment_name || rate.name}: ${money}`;
          list.append(item);
        }
        this.show('ready');
        this.result.append(list);
      } catch (error) {
        if (!signal.aborted) this.show('error');
        else if (signal.reason?.message === 'timeout' && this.request === controller) this.show('timeout');
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) {
          this.button.disabled = false;
          this.removeAttribute('aria-busy');
        }
      }
    }
  });
}
